import {useCallback} from "react";
import {useTranslations} from "next-intl";
import {useIntlLocale} from "@/src/i18n/useIntlLocale";
import type {CompetitionDto} from "@/src/entities/competition/model/types";

export type DeclarationStatusTone = 'open' | 'soon' | 'closed';

export interface DeclarationStatus {
    tone: DeclarationStatusTone;
    label: string;
}

/**
 * Короткий человекочитаемый статус приёма заявок спортсменов —
 * для бейджа в списке соревнований и на карточке соревнования.
 */
export function useGetDeclarationStatus() {
    const t = useTranslations("declaration.status");
    const locale = useIntlLocale();

    return useCallback((competition: CompetitionDto): DeclarationStatus => {
        const {declaration_permitted, declaration_permitted_reason_code, declaration_datetime_from} =
            competition.settings;

        if (declaration_permitted) {
            return {tone: 'open', label: t("open")};
        }

        if (declaration_permitted_reason_code === 'not_started') {
            const from = declaration_datetime_from
                ? new Date(declaration_datetime_from).toLocaleDateString(locale, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                  })
                : null;
            return {tone: 'soon', label: from ? t("opensAt", {date: from}) : t("opensSoon")};
        }

        if (declaration_permitted_reason_code === 'closed') {
            return {tone: 'closed', label: t("finished")};
        }

        return {tone: 'closed', label: t("closed")};
    }, [t, locale]);
}
